import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Package, Boxes, CheckCircle2, AlertTriangle, Loader2, Power, PlugZap, Filter, Sparkles, ArrowUpCircle, Download, Upload, Gauge, ShieldCheck, ClipboardCheck, Rocket, Sliders, Check, ChevronsUpDown, Search } from 'lucide-react';
import { toast } from 'sonner';

// Shows which dashboards/reports/alerts use a pack's calcs before a disable/upgrade.
function ImpactSummary({ packId, modelId, verb }) {
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    let live = true;
    api.packImpact(packId, modelId).then((d) => { if (live) setImpact(d); }).catch(() => {});
    return () => { live = false; };
  }, [packId, modelId]);
  if (!impact) return null;
  if (impact.impact_count === 0) {
    return <div className="text-[11px] text-muted-foreground" data-testid="pack-impact">Not used by any dashboards, reports or alerts — safe to {verb}.</div>;
  }
  const groups = [
    ['dashboards', impact.dashboards], ['reports', impact.reports], ['alerts', impact.alerts],
  ].filter(([, arr]) => arr.length);
  return (
    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-2 text-[11px] space-y-1" data-testid="pack-impact">
      <div className="flex items-center gap-1.5 text-amber-400 font-medium">
        <Gauge className="w-3.5 h-3.5" /> Used by {impact.impact_count} item{impact.impact_count > 1 ? 's' : ''} — review before you {verb}
      </div>
      {groups.map(([label, arr]) => (
        <div key={label} className="text-muted-foreground ml-5">
          <span className="capitalize text-foreground">{arr.length} {label}:</span> {arr.map((x) => x.name).join(', ')}
        </div>
      ))}
    </div>
  );
}

const CONFLICT_LABEL = {
  same_name_different_formula: 'same name, different formula',
  different_unit: 'same name, different unit',
  same_concept_different_name: 'same definition, different name',
};

const CHANGE_LABEL = {
  new: 'New in this version', pack_update: 'Pack update', customized: 'You customised this',
  unchanged: 'Unchanged', removed: 'Removed in this version',
};
const CHANGE_STYLE = {
  new: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  pack_update: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  customized: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  unchanged: 'bg-muted text-muted-foreground border-border/40',
  removed: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
};

const statusBadge = (s) => {
  if (s === 'active') return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
  if (s === 'review_required') return 'bg-violet-500/15 text-violet-400 border-violet-500/20';
  if (s === 'validation_required') return 'bg-amber-500/15 text-amber-400 border-amber-500/20';
  if (s === 'mapping_required') return 'bg-orange-500/15 text-orange-400 border-orange-500/20';
  if (s === 'staged') return 'bg-blue-500/15 text-blue-400 border-blue-500/20';
  if (s === 'blocked') return 'bg-rose-500/15 text-rose-400 border-rose-500/20';
  if (s === 'disabled') return 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20';
  return 'bg-muted text-muted-foreground border-border/40';
};

const CERT_STYLE = {
  certified: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  in_review: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  rejected: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
  draft: 'bg-muted text-muted-foreground border-border/40',
};

// Step 4 — configure pack parameters (only rendered when the pack declares any).
function ParametersSection({ pack, modelId, params, saved, onChanged }) {
  const [values, setValues] = useState(saved || {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.configurePack(pack.pack_id, { model_id: modelId, parameters: values }); toast.success('Parameters saved'); onChanged(); }
    catch (e) { toast.error(e.message || 'Configure failed'); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="pack-parameters">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Sliders className="w-3 h-3" /> Parameters &amp; rules</p>
      <div className="space-y-2">
        {params.map((pd) => (
          <div key={pd.parameter_id} className="grid grid-cols-[1fr_1.3fr] gap-2 items-center" data-testid={`pack-param-${pd.parameter_id}`}>
            <span className="text-xs text-foreground">{pd.name || pd.parameter_id}{pd.required && <span className="text-rose-400"> *</span>}</span>
            <input value={values[pd.parameter_id] ?? pd.default ?? ''} onChange={(e) => setValues((v) => ({ ...v, [pd.parameter_id]: e.target.value }))}
              placeholder={pd.description || pd.data_type || ''} className="h-8 rounded-lg border border-border/40 bg-card px-2 text-[11px]" />
          </div>
        ))}
      </div>
      <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={save} data-testid="pack-configure-save-btn">Save parameters</Button>
    </div>
  );
}

// Per-calculation readiness + gated activation pipeline (pilot §9).
function CalcReadinessList({ pack, modelId, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try { setData(await api.packReadiness(pack.pack_id, modelId)); } catch { /* ignore */ }
  }, [pack.pack_id, modelId]);
  useEffect(() => { load(); }, [load]);

  const activate = async (calcId) => {
    setBusy(calcId);
    try {
      const r = await api.activatePackCalc(pack.pack_id, { model_id: modelId, calc_id: calcId });
      if (r.status === 'blocked') toast.error('Blocked — resolve requirements first');
      else toast.success('Activated as draft — routed for certification');
      await load(); onChanged();
    } catch (e) { toast.error(e.message || 'Activation failed'); } finally { setBusy(''); }
  };

  if (!data) return null;
  return (
    <div data-testid="pack-readiness">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
        Calculations &amp; readiness — {data.summary.available}/{data.summary.total} available
      </p>
      <div className="rounded-lg border border-border/40 divide-y divide-border/10">
        {data.calculations.map((c) => {
          const u = c.unresolved || {};
          const isOpen = open === c.calc_id;
          return (
            <div key={c.calc_id} className="px-3 py-2" data-testid={`readiness-${c.calc_id}`}>
              <div className="flex items-center gap-2">
                <button className="text-xs text-foreground font-medium flex-1 text-left" onClick={() => setOpen(isOpen ? null : c.calc_id)}>
                  {c.name}
                </button>
                <Badge className={`text-[8px] border ${c.available ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20'}`}>
                  {c.available ? 'available' : 'blocked'}
                </Badge>
                {c.certification_status && c.certification_status !== 'not_activated' && (
                  <Badge className={`text-[8px] border ${CERT_STYLE[c.certification_status] || ''}`}>{c.certification_status}</Badge>
                )}
                {c.available && c.activation_status !== 'active' && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]" disabled={busy === c.calc_id}
                    onClick={() => activate(c.calc_id)} data-testid={`readiness-activate-${c.calc_id}`}>
                    {busy === c.calc_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Activate'}
                  </Button>
                )}
              </div>
              <div className="text-[10px] font-mono text-amber-400/90 break-words mt-0.5">{c.formula}</div>
              {isOpen && (
                <div className="mt-1.5 space-y-1 text-[10px] text-muted-foreground">
                  <div>{c.description}</div>
                  <div>Grain: <span className="text-foreground">{c.grain || '—'}</span> · Date role: <span className="text-foreground">{c.default_date_role || '—'}</span> · Returns: <span className="text-foreground">{c.return_type || '—'}</span></div>
                  <div>Required concepts: {c.required_concepts.map((rc) => (
                    <span key={rc.concept} className={rc.mapped_to ? 'text-emerald-400' : 'text-rose-400'}>{rc.name}{rc.mapped_to ? `→${rc.mapped_to}` : ''}{' '}</span>
                  ))}</div>
                  {c.parameters.length > 0 && <div>Parameters: <span className="text-foreground">{c.parameters.join(', ')}</span></div>}
                  {c.governed_groups.length > 0 && <div>Governed groups: <span className="text-foreground">{c.governed_groups.join(', ')}</span></div>}
                  {c.calc_dependencies.length > 0 && <div>Depends on: <span className="text-foreground">{c.calc_dependencies.join(', ')}</span></div>}
                  {!c.available && (
                    <div className="text-rose-400" data-testid={`readiness-missing-${c.calc_id}`}>
                      Missing: {[
                        ...(u.missing_concepts || []).map((m) => `concept ${m.name}`),
                        ...(u.missing_groups || []).map((m) => `group ${m.group}`),
                        ...(u.missing_parameters || []).map((m) => `param ${m}`),
                        ...(u.missing_functions || []).map((m) => `fn ${m}`),
                        ...(u.missing_calc_dependencies || []).map((m) => `calc ${m.ref}`),
                        ...(u.date_role_resolved ? [] : ['date role']),
                      ].join(', ')}
                    </div>
                  )}
                  <div>Lineage: pack {c.lineage.pack_id}@{c.lineage.pack_version}{c.lineage.measure_id ? ` · measure ${c.lineage.measure_id}` : ''}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Steps 5–7 — validate, certify and publish an installed pack's governed calcs (§9/§18).
function WorkflowSection({ pack, modelId, onChanged }) {
  const [gov, setGov] = useState(null);
  const [busy, setBusy] = useState('');
  const [reason, setReason] = useState('');
  const load = useCallback(async () => {
    try { setGov(await api.packGovernance(pack.pack_id, modelId)); } catch { /* ignore */ }
  }, [pack.pack_id, modelId]);
  useEffect(() => { load(); }, [load]);

  const validate = async () => {
    setBusy('validate');
    try {
      const r = await api.validatePackInstall(pack.pack_id, { model_id: modelId });
      const fn = r.status === 'blocked' ? toast.error : r.status === 'warnings' ? toast.warning : toast.success;
      fn(`Validation ${r.status} — ${r.error_count} error(s), ${r.warning_count} warning(s)`);
      await load(); onChanged();
    } catch (e) { toast.error(e.message || 'Validation failed'); } finally { setBusy(''); }
  };
  const certify = async () => {
    if (!reason.trim()) { toast.error('Add a reason to certify'); return; }
    setBusy('certify');
    try {
      const r = await api.certifyPackCalc(pack.pack_id, { model_id: modelId, decision: 'certify', reason });
      toast.success(`Certified ${r.calcs.length} calculation(s)`); setReason('');
      await load(); onChanged();
    } catch (e) { toast.error(e.message || 'Certify failed'); } finally { setBusy(''); }
  };
  const publish = async () => {
    setBusy('publish');
    try {
      const r = await api.publishPack(pack.pack_id, { model_id: modelId });
      toast.success(r.count ? `Published ${r.count} certified calc(s)` : 'Nothing certified to publish yet');
      await load(); onChanged();
    } catch (e) { toast.error(e.message || 'Publish failed'); } finally { setBusy(''); }
  };

  if (!gov) return null;
  const mc = gov.mapping_completeness || {};
  const cc = gov.certification_counts || {};
  return (
    <div className="rounded-lg border border-border/40 p-3 space-y-3" data-testid="pack-workflow">
      <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /><span className="text-xs font-semibold text-foreground">Governance &amp; certification</span></div>
      <div className="grid grid-cols-2 gap-y-1 gap-x-3 text-[11px] text-muted-foreground">
        <div>Mapping: <span className="text-foreground">{mc.mapped}/{mc.required} required</span></div>
        <div>Validation: <span className="text-foreground capitalize">{gov.validation_status || '—'}</span></div>
        <div>Publish: <span className="text-foreground">{gov.publish_status || 'none'}</span></div>
        <div className="flex items-center gap-1 flex-wrap">Calcs:{Object.keys(cc).length ? Object.entries(cc).map(([k, v]) => <Badge key={k} className={`text-[8px] border ${CERT_STYLE[k] || ''}`}>{v} {k}</Badge>) : <span>none</span>}</div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" disabled={busy === 'validate'} onClick={validate} data-testid="pack-validate-btn">{busy === 'validate' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ClipboardCheck className="w-3.5 h-3.5 mr-1" />}Validate</Button>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Certification reason" className="flex-1 min-w-[140px] h-8 rounded-lg border border-border/40 bg-card px-2 text-[11px]" data-testid="pack-certify-reason" />
        <Button size="sm" variant="outline" disabled={busy === 'certify'} onClick={certify} data-testid="pack-certify-btn">{busy === 'certify' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}Certify calcs</Button>
        <Button size="sm" disabled={busy === 'publish'} onClick={publish} data-testid="pack-publish-btn">{busy === 'publish' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1" />}Publish</Button>
      </div>
    </div>
  );
}

function FieldCombobox({ value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.name === value);
  const fieldOpts = options.filter((o) => o.kind !== 'measure');
  const measureOpts = options.filter((o) => o.kind === 'measure');
  const renderItem = (f) => (
    <CommandItem key={`${f.kind}:${f.name}`} value={`${f.display} ${f.name}`}
      onSelect={() => { onChange(f.name); setOpen(false); }}
      className="text-xs" data-testid={`${testid}-opt-${f.name}`}>
      <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${value === f.name ? 'opacity-100' : 'opacity-0'}`} />
      <span className="truncate flex-1">
        {f.display}
        {f.display !== f.name && <span className="ml-1 text-[9px] text-muted-foreground">· {f.name}</span>}
      </span>
      {f.data_type ? <span className="ml-auto pl-2 text-[9px] text-muted-foreground shrink-0">{f.data_type}</span> : null}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className="h-8 w-full justify-between text-xs font-normal" data-testid={testid}>
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>{selected ? selected.display : (value || '— choose field —')}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[240px]" align="start">
        <Command>
          <CommandInput placeholder="Search fields & measures…" className="h-9 text-xs" data-testid={`${testid}-search`} />
          <CommandList className="max-h-64" onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }}>
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">No match found.</CommandEmpty>
            {fieldOpts.length > 0 && <CommandGroup heading="Fields">{fieldOpts.map(renderItem)}</CommandGroup>}
            {measureOpts.length > 0 && <CommandGroup heading="Measures">{measureOpts.map(renderItem)}</CommandGroup>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CategoryMultiSelect({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (c) => onChange(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]);
  const label = selected.length === 0 ? 'All industries' : selected.length === 1 ? selected[0] : `${selected.length} industries`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" role="combobox" aria-expanded={open}
          className="min-w-[190px] justify-between text-xs font-normal" data-testid="pack-cat-multiselect">
          <span className="flex items-center gap-1.5 truncate"><Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />{label}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[240px]" align="end">
        <Command>
          <CommandInput placeholder="Search industries…" className="h-9 text-xs" data-testid="pack-cat-search" />
          <CommandList className="max-h-72" onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }}>
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">No industry found.</CommandEmpty>
            <CommandGroup>
              {options.map((c) => (
                <CommandItem key={c} value={c} onSelect={() => toggle(c)} className="text-xs" data-testid={`pack-cat-opt-${c}`}>
                  <div className={`mr-2 flex h-3.5 w-3.5 items-center justify-center rounded border ${selected.includes(c) ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
                    {selected.includes(c) && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <span className="truncate">{c}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {selected.length > 0 && (
            <div className="border-t border-border/40 p-1">
              <button onClick={() => onChange([])} className="w-full text-center text-[11px] text-muted-foreground hover:text-foreground py-1.5" data-testid="pack-cat-clear">Clear ({selected.length})</button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function GovernedValuesSection({ pack, modelId, onChanged }) {
  const [groups, setGroups] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.packGovernedGroups(pack.pack_id, modelId);
      const gs = r.governed_groups || [];
      setGroups(gs);
      const d = {};
      gs.forEach((g) => { d[g.group_id] = (g.values.length ? g.values : (g.suggested_values || [])).join(', '); });
      setDrafts(d);
    } catch (e) { toast.error(e.message || 'Failed to load governed groups'); }
  }, [pack.pack_id, modelId]);

  useEffect(() => { load(); }, [load]);

  const save = async (g, certify) => {
    const values = (drafts[g.group_id] || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!values.length) { toast.error('Enter at least one source value'); return; }
    setBusy(g.group_id);
    try {
      await api.mapPackGroup(pack.pack_id, { model_id: modelId, group_id: g.group_id, values, certify });
      toast.success(certify ? `Certified "${g.name}"` : `Saved "${g.name}"`);
      await load();
      onChanged();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setBusy(''); }
  };

  if (!groups || groups.length === 0) return null;
  return (
    <div data-testid="pack-governed-values">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Governed values — map source values into groups</p>
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.group_id} className="rounded-lg border border-border/40 p-2.5" data-testid={`pack-group-${g.group_id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-foreground">{g.name}</span>
              {g.mapped
                ? <Badge className={`text-[8px] ${g.certification_status === 'certified' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/15 text-amber-400 border-amber-500/20'}`}>{g.certification_status === 'certified' ? 'certified' : 'draft'}</Badge>
                : <Badge className="text-[8px] bg-rose-500/15 text-rose-400 border-rose-500/20">unmapped</Badge>}
              {g.owner && <span className="text-[9px] text-muted-foreground ml-auto">owner: {g.owner}</span>}
            </div>
            {g.description && <div className="text-[10px] text-muted-foreground mb-1.5">{g.description}</div>}
            <input
              value={drafts[g.group_id] || ''}
              onChange={(e) => setDrafts((d) => ({ ...d, [g.group_id]: e.target.value }))}
              placeholder="Source values, comma-separated (e.g. NEW, NB, N)"
              className="w-full h-8 px-2 rounded-lg bg-muted/40 border border-border/50 text-xs text-foreground outline-none focus:border-primary/50"
              data-testid={`pack-group-input-${g.group_id}`}
            />
            <div className="flex gap-2 mt-1.5">
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy === g.group_id} onClick={() => save(g, false)} data-testid={`pack-group-save-${g.group_id}`}>
                {busy === g.group_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save (draft)'}
              </Button>
              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500" disabled={busy === g.group_id} onClick={() => save(g, true)} data-testid={`pack-group-certify-${g.group_id}`}>
                Certify
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConfigureDialog({ pack, modelId, fields, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [mappings, setMappings] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [suggesting, setSuggesting] = useState(false);

  const runAutoMap = useCallback(async (applyAll) => {
    setSuggesting(true);
    try {
      const r = await api.suggestPackMappings(pack.pack_id, { model_id: modelId });
      setSuggestions(r.suggestions || {});
      if (applyAll) {
        setMappings((m) => {
          const next = { ...m };
          Object.entries(r.suggestions || {}).forEach(([k, v]) => { if (v.top && !next[k]) next[k] = v.top.field; });
          return next;
        });
      }
      return r.suggestions || {};
    } catch (e) { toast.error(e.message || 'Auto-map failed'); return {}; }
    finally { setSuggesting(false); }
  }, [pack.pack_id, modelId]);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.getPack(pack.pack_id, modelId);
        setDetail(d);
        const saved = (d.installation && d.installation.mappings) || {};
        if (Object.keys(saved).length) {
          setMappings(saved);
          runAutoMap(false);
        } else {
          // Fresh install → smart auto-map fills the mappings.
          const sug = await runAutoMap(false);
          const auto = {};
          Object.entries(sug).forEach(([k, v]) => { if (v.top) auto[k] = v.top.field; });
          setMappings(auto);
        }
      } catch (e) { toast.error(e.message || 'Failed to load pack'); }
    })();
  }, [pack.pack_id, modelId, runAutoMap]);

  const requiredUnmapped = useMemo(
    () => (detail?.concepts || []).filter((c) => c.required && !mappings[c.key]).map((c) => c.name),
    [detail, mappings]);

  const install = async () => {
    setBusy(true);
    try {
      await api.installPack(pack.pack_id, { model_id: modelId, is_primary_industry: pack.pack_type === 'industry', mappings });
      toast.success('Mappings saved');
      const d = await api.getPack(pack.pack_id, modelId);
      setDetail(d);
      onChanged();
    } catch (e) { toast.error(e.message || 'Install failed'); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    setBusy(true); setResult(null);
    try {
      await api.installPack(pack.pack_id, { model_id: modelId, is_primary_industry: pack.pack_type === 'industry', mappings });
      const r = await api.activatePack(pack.pack_id, { model_id: modelId });
      setResult(r);
      if (r.created?.length || r.updated?.length) toast.success(`Activated — ${r.created.length + r.updated.length} calculation(s) added`);
      else if (r.conflicts?.length) toast.warning('Activated with conflicts — see details');
      else toast.message('Nothing to activate yet — map more concepts');
      onChanged();
    } catch (e) { toast.error(e.message || 'Activate failed'); }
    finally { setBusy(false); }
  };

  const installed = detail?.installation;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pack-configure-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4 text-primary" /> {pack.name}
          </DialogTitle>
          <DialogDescription className="text-xs">Map each concept to a field, then activate to add governed draft calculations.</DialogDescription>
        </DialogHeader>
        {!detail ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">{detail.description}</p>
            {detail.install_state && detail.install_state !== 'available' && (
              <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/30 px-3 py-2" data-testid="pack-install-state">
                <Badge className={`text-[9px] border ${statusBadge(detail.install_state)}`}>{detail.install_state_label}</Badge>
                <span className="text-[11px] text-muted-foreground">{detail.install_state_reason}</span>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Map concepts to your model fields</p>
                <Button size="sm" variant="ghost" className="h-6 text-[11px] gap-1 text-primary" disabled={suggesting} onClick={() => runAutoMap(true)} data-testid="pack-automap-btn">
                  {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Smart auto-map
                </Button>
              </div>
              <div className="space-y-2">
                {detail.concepts.map((c) => {
                  const sug = suggestions[c.key];
                  const top = sug && sug.top;
                  const conf = top ? Math.round(top.confidence * 100) : null;
                  return (
                  <div key={c.key} className="grid grid-cols-[1fr_1.3fr] gap-2 items-center" data-testid={`pack-concept-${c.key}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-foreground truncate" title={c.description}>{c.name}</span>
                        {c.required && <Badge className="text-[8px] bg-rose-500/15 text-rose-400 border-rose-500/20">required</Badge>}
                      </div>
                      {top ? (
                        <span className="text-[9px] text-primary/80 flex items-center gap-1" title={(top.reasons || []).join(', ')} data-testid={`pack-suggestion-${c.key}`}>
                          <Sparkles className="w-2.5 h-2.5" /> {conf}% match{top.reasons?.some((r) => r.startsWith('profiled')) ? ' · profiled' : ''}
                        </span>
                      ) : <span className="text-[10px] text-muted-foreground capitalize">{c.type}</span>}
                    </div>
                    <FieldCombobox
                      value={mappings[c.key] || ''}
                      options={fields || []}
                      onChange={(v) => setMappings((m) => ({ ...m, [c.key]: v }))}
                      testid={`pack-map-${c.key}`}
                    />
                  </div>
                  );
                })}
              </div>
            </div>

            {installed && <GovernedValuesSection pack={pack} modelId={modelId} onChanged={onChanged} />}

            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Calculations in this pack</p>
              {installed ? (
                <CalcReadinessList pack={pack} modelId={modelId} onChanged={onChanged} />
              ) : (
                <div className="rounded-lg border border-border/40 divide-y divide-border/10">
                  {detail.calculations.map((cc) => (
                    <div key={cc.key} className="px-3 py-1.5" data-testid={`pack-calc-${cc.key}`}>
                      <div className="text-xs text-foreground">{cc.name}</div>
                      <div className="text-[10px] font-mono text-amber-400/90 break-words">{cc.formula}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {detail.parameters?.length > 0 && (
              <ParametersSection pack={pack} modelId={modelId} params={detail.parameters}
                saved={installed?.parameter_values} onChanged={onChanged} />
            )}

            {installed && <WorkflowSection pack={pack} modelId={modelId} onChanged={onChanged} />}

            {requiredUnmapped.length > 0 && (
              <div className="flex items-start gap-1.5 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-px flex-shrink-0" /> Map required concept(s) before activating: {requiredUnmapped.join(', ')}
              </div>
            )}

            {result && (
              <div className="rounded-lg border border-border/40 p-2.5 space-y-1 text-[11px]" data-testid="pack-activate-result">
                {(result.created?.length > 0) && <div className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Added: {result.created.join(', ')}</div>}
                {(result.updated?.length > 0) && <div className="text-blue-400">Updated: {result.updated.join(', ')}</div>}
                {(result.conflicts?.length > 0) && (
                  <div className="text-rose-400" data-testid="pack-conflicts">
                    <div className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Conflicts (review before certifying):</div>
                    {result.conflicts.map((c, i) => (
                      <div key={`${c.name}-${c.type}-${i}`} className="ml-4 text-[10px]">
                        <span className="font-mono text-rose-300">{c.name}</span>
                        <span className="text-muted-foreground"> — {CONFLICT_LABEL[c.type] || c.type}</span>
                        {c.existing_name && c.existing_name !== c.name && <span className="text-muted-foreground"> (vs “{c.existing_name}”)</span>}
                      </div>
                    ))}
                  </div>
                )}
                {(result.skipped?.length > 0) && <div className="text-muted-foreground">Skipped (unmapped): {result.skipped.map((s) => s.name).join(', ')}</div>}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          {installed && (
            <div className="w-full mb-1"><ImpactSummary packId={pack.pack_id} modelId={modelId} verb="disable" /></div>
          )}
          {installed && (
            <Button variant="outline" disabled={busy} onClick={async () => {
              try {
                const art = await api.exportPack(pack.pack_id, modelId);
                const blob = new Blob([JSON.stringify(art, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${pack.pack_id}-config.json`; a.click();
                URL.revokeObjectURL(url);
                toast.success('Pack configuration exported');
              } catch (e) { toast.error(e.message || 'Export failed'); }
            }} data-testid="pack-export-btn"><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
          )}
          {installed?.activation_status === 'active' && (
            <Button variant="outline" disabled={busy} onClick={async () => { setBusy(true); try { const r = await api.disablePack(pack.pack_id, { model_id: modelId }); toast.success(`Disabled — removed ${r.removed_measures} calc(s)`); onChanged(); onClose(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } }} data-testid="pack-disable-btn">
              <Power className="w-3.5 h-3.5 mr-1" /> Disable
            </Button>
          )}
          <Button variant="outline" disabled={busy} onClick={install} data-testid="pack-install-btn">Save mappings</Button>
          <Button disabled={busy || requiredUnmapped.length > 0} onClick={activate} data-testid="pack-activate-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PlugZap className="w-3.5 h-3.5 mr-1" />} Activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpgradeDialog({ pack, modelId, onClose, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.upgradePreview(pack.pack_id, modelId);
        setPreview(p);
        const d = {};
        (p.changes || []).forEach((c) => { if (c.change === 'customized') d[c.calc_key] = c.recommended || 'keep_mine'; });
        setDecisions(d);
      } catch (e) { toast.error(e.message || 'Failed to load upgrade'); }
    })();
  }, [pack.pack_id, modelId]);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.upgradePack(pack.pack_id, { model_id: modelId, decisions });
      toast.success(`Upgraded to v${r.to_version} — ${r.applied.length + r.added.length} calc(s) applied, ${r.kept_custom.length} kept`);
      onChanged(); onClose();
    } catch (e) { toast.error(e.message || 'Upgrade failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pack-upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="w-4 h-4 text-primary" /> Upgrade {pack.name}
            {preview && <span className="text-xs text-muted-foreground">v{preview.installed_version} → v{preview.latest_version}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs">Review each change; your customised calculations are never overwritten unless you choose to take the pack version.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-8 flex items-center justify-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Comparing versions…</div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Your customised calculations are never overwritten unless you choose “Take pack version”.</p>
            <ImpactSummary packId={pack.pack_id} modelId={modelId} verb="upgrade" />
            {preview.changes.map((c) => (
              <div key={c.calc_key} className="rounded-lg border border-border/40 p-2.5" data-testid={`upgrade-change-${c.calc_key}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-foreground">{c.name}</span>
                  <Badge className={`text-[8px] border ${CHANGE_STYLE[c.change]}`}>{CHANGE_LABEL[c.change] || c.change}</Badge>
                </div>
                {c.change === 'pack_update' && (
                  <div className="mt-1 text-[10px] font-mono space-y-0.5">
                    <div className="text-muted-foreground line-through break-words">{c.current_formula}</div>
                    <div className="text-emerald-400 break-words">{c.incoming_formula}</div>
                  </div>
                )}
                {c.change === 'new' && <div className="mt-1 text-[10px] font-mono text-emerald-400 break-words">{c.incoming_formula}</div>}
                {c.change === 'customized' && (
                  <div className="mt-1.5 space-y-1.5">
                    <div className="text-[10px] font-mono text-amber-400 break-words">Yours: {c.current_formula}</div>
                    <div className="text-[10px] font-mono text-blue-400 break-words">Pack v{preview.latest_version}: {c.incoming_formula}</div>
                    <div className="flex gap-3 mt-1">
                      {['keep_mine', 'take_theirs'].map((opt) => (
                        <label key={opt} className="flex items-center gap-1 text-[11px] cursor-pointer" data-testid={`upgrade-${c.calc_key}-${opt}`}>
                          <input type="radio" name={`d-${c.calc_key}`} checked={(decisions[c.calc_key] || 'keep_mine') === opt} onChange={() => setDecisions((d) => ({ ...d, [c.calc_key]: opt }))} />
                          {opt === 'keep_mine' ? 'Keep mine' : 'Take pack version'}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !preview?.upgrade_available} onClick={apply} data-testid="pack-upgrade-apply-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5 mr-1" />} Apply upgrade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ models, defaultModelId, onClose, onChanged }) {
  const [modelId, setModelId] = useState(defaultModelId || '');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const doImport = async () => {
    let artifact;
    try { artifact = JSON.parse(text); } catch { toast.error('That is not valid JSON'); return; }
    if (!artifact.pack_id) { toast.error('Artifact is missing pack_id'); return; }
    if (!modelId) { toast.error('Choose a target model'); return; }
    setBusy(true);
    try {
      const r = await api.importPack(artifact.pack_id, { model_id: modelId, artifact_data: artifact });
      const created = (r.activated?.created || []).length;
      toast.success(`Imported ${artifact.pack_id} — ${created} calc(s), ${r.overrides_applied.length} override(s)`);
      onChanged(); onClose();
    } catch (e) { toast.error(e.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) setText(await f.text());
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pack-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-primary" /> Import pack configuration</DialogTitle>
          <DialogDescription className="text-xs">Paste or upload a pack-config JSON exported from another workspace or tenant. It will install the mappings and re-apply overrides on the chosen model.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger data-testid="import-model-select"><SelectValue placeholder="Target semantic model" /></SelectTrigger>
            <SelectContent>{models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <input type="file" accept="application/json,.json" onChange={onFile} className="text-xs" data-testid="import-file" />
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="…or paste the exported JSON here"
            className="w-full h-40 rounded-lg border border-border/40 bg-card p-2 text-[11px] font-mono" data-testid="import-json" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !text.trim()} onClick={doImport} data-testid="import-apply-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />} Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CalculationPacksPage() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [packs, setPacks] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [upgrading, setUpgrading] = useState(null);
  const [importing, setImporting] = useState(false);
  const [category, setCategory] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const ms = await api.getSemanticModels();
        const list = Array.isArray(ms) ? ms : [];
        setModels(list);
        if (list.length) setModelId(list[0].model_id);
      } catch (e) { toast.error(e.message || 'Failed to load models'); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadPacks = useCallback(async () => {
    if (!modelId) return;
    try {
      const [res, ents, meas] = await Promise.all([
        api.listPacks(modelId),
        api.getSemanticEntities(modelId).catch(() => []),
        api.getSemanticMeasures(modelId).catch(() => []),
      ]);
      setPacks(res.packs || []);
      const flat = [];
      const seenNames = new Set();
      (Array.isArray(ents) ? ents : []).forEach((e) => (e.attributes || []).forEach((a) => {
        if (a.name && !seenNames.has(a.name)) {
          seenNames.add(a.name);
          flat.push({ name: a.name, display: a.business_name || a.name, data_type: a.data_type || '', kind: 'field' });
        }
      }));
      flat.sort((a, b) => a.display.localeCompare(b.display, undefined, { sensitivity: 'base' }));
      const measList = (Array.isArray(meas) ? meas : [])
        .filter((m) => m.name)
        .map((m) => ({ name: m.name, display: m.name, data_type: m.data_type || '', kind: 'measure' }))
        .sort((a, b) => a.display.localeCompare(b.display, undefined, { sensitivity: 'base' }));
      setFields([...flat, ...measList]);
    } catch (e) { toast.error(e.message || 'Failed to load packs'); }
  }, [modelId]);

  useEffect(() => { loadPacks(); }, [loadPacks]);

  const categories = useMemo(() => Array.from(new Set(packs.map((p) => p.primary_category).filter(Boolean))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })), [packs]);
  const q = query.trim().toLowerCase();
  const base = q
    ? packs.filter((p) => `${p.name || ''} ${p.description || ''} ${p.primary_category || ''}`.toLowerCase().includes(q))
    : (category.length === 0 ? packs : packs.filter((p) => category.includes(p.primary_category)));
  const shown = base
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="calc-packs-page">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight flex items-center gap-3">
          <Package className="w-7 h-7 text-primary" /> Calculation Packs
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Install industry &amp; business-function calculation packs, map their concepts to your model's fields,
          and activate governed calculations — all built from the generic calculation engine. Activated calcs
          land as draft measures for your stewards to certify.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="min-w-[240px]">
          <Select value={modelId} onValueChange={setModelId}>
            <SelectTrigger data-testid="pack-model-select"><SelectValue placeholder="Choose a semantic model" /></SelectTrigger>
            <SelectContent>
              {models.map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search packs by name…"
            className="w-full h-9 pl-8 pr-3 rounded-lg bg-muted/40 border border-border/50 text-xs text-foreground outline-none focus:border-primary/50"
            data-testid="pack-search"
          />
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <Button size="sm" variant="outline" onClick={() => setImporting(true)} data-testid="pack-import-open-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import config
          </Button>
          <CategoryMultiSelect options={categories} selected={category} onChange={setCategory} />
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && shown.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shown.map((p) => (
            <div key={p.pack_id} className="rounded-xl border border-border/60 bg-card p-4 flex flex-col" data-testid={`pack-card-${p.pack_id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"><Boxes className="w-4.5 h-4.5 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-heading font-semibold text-foreground">{p.name}</h3>
                    <Badge className={`text-[9px] border ${statusBadge(p.install_state || p.status)}`} data-testid={`pack-status-${p.pack_id}`} title={p.install_state_reason || ''}>{p.install_state_label || (p.status || '').replace('_', ' ')}</Badge>
                    {p.is_primary_industry && <Badge className="text-[8px] bg-primary/15 text-primary border-primary/20">primary</Badge>}
                    {p.upgrade_available && <Badge className="text-[8px] bg-amber-500/15 text-amber-400 border-amber-500/20 gap-1" data-testid={`pack-upgrade-badge-${p.pack_id}`}><ArrowUpCircle className="w-2.5 h-2.5" />upgrade</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-3">
                <span className="capitalize">{p.pack_type.replace('_', ' ')}</span>
                <span>· {p.primary_category}</span>
                <span>· {p.calculation_count} calcs</span>
                <span>· v{p.version}</span>
              </div>
              {p.install_state && p.install_state !== 'available' && p.install_state_reason && (
                <p className="text-[10px] text-muted-foreground/90 mt-1.5" data-testid={`pack-state-reason-${p.pack_id}`}>{p.install_state_reason}</p>
              )}
              <div className="mt-3 pt-3 border-t border-border/30 flex justify-end gap-2">
                {p.upgrade_available && (
                  <Button size="sm" variant="outline" className="text-amber-400 border-amber-500/30" onClick={() => setUpgrading(p)} data-testid={`pack-upgrade-${p.pack_id}`}>
                    <ArrowUpCircle className="w-3.5 h-3.5 mr-1" /> Upgrade
                  </Button>
                )}
                <Button size="sm" variant={p.install_state === 'active' ? 'outline' : 'default'} onClick={() => setConfiguring(p)} data-testid={`pack-configure-${p.pack_id}`}>
                  {p.install_state === 'active' ? 'Manage' : (p.install_state && p.install_state !== 'available') ? 'Continue setup' : 'Configure & install'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {configuring && modelId && (
        <ConfigureDialog pack={configuring} modelId={modelId} fields={fields}
          onClose={() => setConfiguring(null)} onChanged={loadPacks} />
      )}
      {upgrading && modelId && (
        <UpgradeDialog pack={upgrading} modelId={modelId}
          onClose={() => setUpgrading(null)} onChanged={loadPacks} />
      )}
      {importing && (
        <ImportDialog models={models} defaultModelId={modelId}
          onClose={() => setImporting(false)} onChanged={loadPacks} />
      )}
    </div>
  );
}
